import { useState, useEffect } from "react";
import Badge from "../shared-components/Badge";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { classNames } from "../utils";
import { ReportsPendingReviewItem } from "../api/Reports";
import {
  ReportEntry,
  ReportEntryStyle,
  ReportRowItem,
} from "./helper-components/TableModels";
import ReportsCircleTooltip from "./helper-components/ReportsCircleTooltip";

export type TableHeader = string | { text: string; customClass: string };

interface TableProps {
  data: ReportRowItem<ReportsPendingReviewItem>[];
  header: TableHeader[];
  isLoading: boolean;
}

const TableReviewed = ({ data, header, isLoading }: TableProps) => {
  const [tableData, setTableData] = useState<
    ReportRowItem<ReportsPendingReviewItem>[]
  >(data !== undefined ? data : []);

  useEffect(() => {
    let table: ReportRowItem<ReportsPendingReviewItem>[] = [];
    if (data) {
      table = [...data];
    }
    setTableData(table);
  }, [data]);

  return (
    <>
      <div>
        <div className="flex text-gray-500 rounded-t-xl border shadow shadow-gray-300">
          {header.map((header, idx) => (
            <div
              data-testid="tableHeader"
              className={classNames(
                "flex rounded-t-xl items-center p-3 text-xs leading-4 font-medium bg-gray-50 uppercase",
                typeof header === "string" ? "flex-1" : header.customClass
              )}
              key={idx}
            >
              {typeof header === "string" ? header : header.text}
            </div>
          ))}
        </div>
        <div className="bg-white border-l border-r rounded-b-xl shadow shadow-gray-300">
          {isLoading && <LoadingSpinner />}
          {!isLoading && tableData.length === 0 ? (
            <div>No search Items</div>
          ) : (
            tableData.map((report, i) => (
              <ReportRow reportRow={report} index={i} key={i} />
            ))
          )}
        </div>
      </div>
    </>
  );
};

interface ReportRowProps {
  reportRow: ReportRowItem<ReportsPendingReviewItem>;
  index: number;
}

const ReportRow = ({ reportRow, index }: ReportRowProps) => {
  return (
    <div
      className={classNames(
        "flex border-b text-xs leading-4 font-normal py-4",
        reportRow.isLocked ? "bg-gray-100" : ""
      )}
      key={reportRow.report_uuid || `report-${index}`}
    >
      {reportRow.data.map((entry: ReportEntry, j) => (
        <ReportCell
          reportRow={reportRow}
          entry={entry}
          flagkey={`report-${index}-entry-${j}`}
          key={j}
        />
      ))}
    </div>
  );
};

const ReportCell = ({
  reportRow,
  entry,
  flagkey,
}: {
  reportRow: ReportRowItem<ReportsPendingReviewItem>;
  entry: ReportEntry;
  flagkey: string;
}) => {
  switch (entry.style) {
    case ReportEntryStyle.COMPONENT:
      return (
        <div
          className={classNames(
            "flex px-3 gap-2 items-center",
            entry.customClass ? entry.customClass : "flex-1 w-4 truncate"
          )}
        >
          {entry.component}
        </div>
      );
    case ReportEntryStyle.BADGE:
      return (
        <div className="flex px-3 flex-1 gap-2 items-center truncate">
          <Badge color={entry.badgeColor || "default"} hasDot={false}>
            {entry.text}
          </Badge>
        </div>
      );
    case ReportEntryStyle.BOLD_TEXT:
      return (
        <div className="flex px-3 flex-1 font-semibold items-center truncate">
          {entry.text}
        </div>
      );
    case ReportEntryStyle.TOOLTIP:
      return <ReportsCircleTooltip reportRow={reportRow} flagKey={flagkey} />;
    case ReportEntryStyle.TEXT:
    default:
      return (
        <div className="flex px-3 flex-1 items-center truncate">
          {entry.text}
        </div>
      );
  }
};

export default TableReviewed;
